import React, { useState, useEffect, useRef } from 'react';
import { Steps } from '../presentational/Steps';
import { format } from 'date-fns';
import { getResourceString } from '../kentico/resource-strings';
import _ from 'lodash';
import ReactHtmlParser from "react-html-parser";
import { Collapse } from 'react-bootstrap';
import { DateParser } from '@fnv-core/parsing/date/DateParser';
import { SummaryHelper } from './helpers/SummaryHelper';
import { ValidationControlHelper } from './helpers/ValidationControlHelper';
import { RenderStep } from './components/FormControlRendering';
import withFormState, { FormStateProps } from './states/FormStates';
import DOMPurify from 'dompurify';
import { FormLogicHelper } from './helpers/FormLogicHelper';
import { ContributionHelper } from './helpers/ContributionHelper';

interface WordLidFormProps extends FormStateProps<WordLidFormPageModel> {
  formDefinitions: FormDefinition;
}

const WordLidForm: React.FC<WordLidFormProps> = (state) => {

  const [contentData, setContentData] = useState<ContentData>(null);
  const [prevContributionBody, setPrevContributionBody] = useState<ContributionRateData>(null);
  const [mobileLidmaatschapOpen, setMobileLidmaatschapOpen] = useState<boolean>(false);
  
  if(state.fields.length > 0 && state.formLoading) {
    setTimeout(() => {
      state.setFormLoading(false);
    }, 1000);
  }

  // Initial rendering of visible fields based on initial form data
  useEffect(() => {
    
    state.setDoAfterSaveActions((formData, pageModel) => {
      return (formData, pageModel) => {
        setGtmData(formData, pageModel);
      };
    });
    state.setCustomVisibleConditions(() => ContributionHelper.getVisibleConditionsIntroductionContribution(state));
    state.setFieldManipulations(() => ContributionHelper.getFieldManipulationsIntroductionContribution(state));
    
    state.initForms(state.formDefinitions);

    const contentData: ContentData = {
      titel: document.querySelector(".react-content h1")?.textContent,
      subTitle: document.querySelector(".react-content h2")?.textContent,
      contentAfterForm: document.querySelectorAll(".react-content .js-content-after-form")?.length > 0,
      content: document.querySelector(".react-content .js-content")?.innerHTML,
      introText: document.querySelector(".react-content .intro")?.textContent,
      showLineBelowTitleBlock: false
    };

    setContentData(contentData);

    // get the invitation id from query params
    const queryParams = FormLogicHelper.getQueryParams();
    if(queryParams["id"]) {
      state.setFormData(prev => ({ ...prev, "UitnodigingID": { value: queryParams["id"], enabled: false, focus: false, loading: false, touched: false } as FormDataEntry }));
    }
  }, []);

  useEffect(() => {

    if(state.formLoading) {
      return;
    }

    if(state.formData["Geboortedatum"]?.touched && ValidationControlHelper.validateField(state.fields, "Geboortedatum", state.formData, state.setFormData, state.handleNewFormData, true)?.length == 0) {
      ContributionHelper.getContribution(state, prevContributionBody, setPrevContributionBody).then((response) => {
        if(response != null) {
          state.setPageModel((prevState) => ({ ...prevState, contribution:response.data }));    
        }
      });
    }   
  }, [state.formData]);

  

  const setGtmData = (formData, pageModel) => {
    let werkvorm = "";
    switch (formData["Situatie"].value) {
      case "werkend": {
        werkvorm = formData["WerkVorm"].value;
        break;
      }
      case "student": {
        werkvorm = formData["StudieVorm"].value;
        break;
      }
      case "werkt_niet": {
        werkvorm = formData["UitkeringVorm"].value;
        break;
      }
    }

    const gaData = {
      event: "purchase",
      ecommerce: {
        currency: "EUR",
        value: (pageModel.contribution.price * 12).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        tax: 0.0, // number
        shipping: 0.0, // number
        affiliation: "FNV",
        transaction_id: self.crypto.randomUUID(),
        items: [
          {
            item_name: `Lidmaatschap - ${formData["Situatie"].value}`,
            affiliation: "FNV.nl",
            item_brand: "FNV",
            item_category: formData["Situatie"].value,
            item_category2: werkvorm,
            item_category3: pageModel.contribution.price.toLocaleString(
              undefined,
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }
            ),
            item_category4:
              formData["WatIsJeBrutoMaandinkomenWerkendUitzendkracht"]?.value,
            price: pageModel.contribution.price * 12,
            quantity: 1,
          },
        ],
      },
    };

    window.dataLayer.push(gaData);
  };

  const formSummaryData: FormSummaryData = {
    sections: [
      {
        title: getResourceString("Form.WerkSituatie.Label"),
        visible: (state.formData["Situatie"]?.value != null),
        navigateToStep: 0,
        rows: [
          {
            fieldName: "Situatie",
            visible: true,
            dataOverride: (data, field) => SummaryHelper.GetOptionLabelFromFieldByKeySummary(field, data)
          },
          {
            fieldName: "WerkVorm",
            visible: state.formData["Situatie"]?.value === "werkend",
            dataOverride: (data, field) => SummaryHelper.GetOptionLabelFromFieldByKeySummary(field, data)
          },
          {
            fieldName: "StudieVorm",
            visible: state.formData["Situatie"]?.value === "student",
            dataOverride: (data, field) => SummaryHelper.GetOptionLabelFromFieldByKeySummary(field, data)
          },
          {
            fieldName: "UitkeringVorm",
            visible: state.formData["Situatie"]?.value === "werkt_niet" || state.formData["WerkVorm"]?.value === "werk_uitkering",
            dataOverride: (data, field) => SummaryHelper.GetOptionLabelFromFieldByKeySummary(field, data)
          },
          {
            fieldName: "WatIsJeBrutoMaandinkomenStuderenDeeltijd",
            visible: state.formData["StudieVorm"]?.value === "deeltijd",
            labelOverride: getResourceString("Form.WatIsJeBrutoMaandinkomen.Label"),
            dataOverride: (data, field) => SummaryHelper.GetOptionLabelFromFieldByKeySummary(field, data)
          },
          {
            fieldName: "WatIsJeBrutoMaandinkomenWerkendUitzendkracht",
            visible: state.formData["WerkVorm"]?.value === "loondienst" || state.formData["WerkVorm"]?.value === "uitzendkracht",
            labelOverride: getResourceString("Form.WatIsJeBrutoMaandinkomen.Label"),
            dataOverride: (data, field) => SummaryHelper.GetOptionLabelFromFieldByKeySummary(field, data)
          }
        ]
      },
      {
        title: getResourceString("Form.JouwGegevens.Label"),
        navigateToStep: 1,
        rows: [
          { 
            fieldName: "Geslacht", 
            visible: true,
            dataOverride: (data, field) => SummaryHelper.GetOptionLabelFromFieldByKeySummary(field, data)
          },
          { 
            fieldName: "Voorletters", 
            visible: true 
          },
          {
            fieldName: "Naam",
            labelOverride: getResourceString("Form.Name.Label"),
            visible: true,
            dataOverride: (data, field) => `${state.formData["Voornaam"].value}${state.formData["Tussenvoegsel"]?.value ? 
              ` ${SummaryHelper.GetOptionLabelFromFieldByKeySummary(state.fields.find((f) => f["@column"] == "Tussenvoegsel"), state.formData["Tussenvoegsel"].value)} ` : 
              ""} ${state.formData["Achternaam"].value}`
          },
          {
            fieldName: "Geboortedatum",
            visible: true,
            dataOverride: (data, field) => format(new DateParser().parse(data), "dd-MM-yyyy")
          },
          { 
            fieldName: "Telefoonnummer", 
            visible: true 
          },
          { 
            fieldName: "E_mailadres", 
            visible: true 
          },
          {
            fieldName: "Adres",
            labelOverride: getResourceString("Form.Adres.Label"),
            visible: true,
            dataOverride: (data, field) => SummaryHelper.getAdresSummary("", state.formData, state.fields)
          }
        ]
      },
      {
        title: getResourceString("Form.WaarWerkJe.Label"),
        navigateToStep: 0,
        visible: 
          (state.formData["Situatie"]?.value === "werkend" && state.formData["WerkVorm"]?.value === "loondienst") || 
          (state.formData["Situatie"]?.value === "student" && state.formData["StudieVorm"]?.value === "deeltijd"),
        rows: [
          { 
            fieldName: "WerkgeverNaam", 
            visible: true 
          },
          { 
            fieldName: "Adres", 
            labelOverride: getResourceString("Form.Adres.Label"),
            visible: true, 
            dataOverride: (data, field) => SummaryHelper.getAdresSummary("Werkgever", state.formData, state.fields) 
          }
        ]
      },
      {
        title: getResourceString("Form.Uitzendbureau.Label"),
        navigateToStep: 0,
        visible: state.formData["Situatie"]?.value === "werkend" && state.formData["WerkVorm"]?.value === "uitzendkracht",
        rows: [
          { 
            fieldName: "UitzendbureauNaam", 
            visible: true 
          },
          { 
            fieldName: "Adres",
            labelOverride: getResourceString("Form.Adres.Label"),
            visible: true, 
            dataOverride: (data, field) => SummaryHelper.getAdresSummary("Uitzendbureau", state.formData, state.fields) 
          }
        ]
      },
      {
        title: getResourceString("Form.Inlener.Label"),
        navigateToStep: 0,
        visible: state.formData["Situatie"]?.value === "werkend" && state.formData["WerkVorm"]?.value === "uitzendkracht",
        rows: [
          { 
            fieldName: "InlenerNaam", 
            visible: true 
          },
          { 
            fieldName: "Adres", 
            labelOverride: getResourceString("Form.Adres.Label"),
            visible: true, 
            dataOverride: (data, field) => SummaryHelper.getAdresSummary("Inlener", state.formData, state.fields) 
          }
        ]
      },
      {
        title: getResourceString("Form.Bedrijfsgegevens.Label"),
        navigateToStep: 0,
        visible: state.formData["Situatie"]?.value === "werkend" && state.formData["WerkVorm"]?.value === "zzp",
        rows: [
          { 
            fieldName: "ZZPBedrijfsnaam", 
            visible: true 
          },
          { 
            fieldName: "Adres",
            labelOverride: getResourceString("Form.Adres.Label"),
            visible: true, 
            dataOverride: (data, field) => SummaryHelper.getAdresSummary("ZZP", state.formData, state.fields) 
          }
        ]
      },
      {
        title: getResourceString("Form.Betaalwijze.Label"),
        navigateToStep: 2,
        rows: [
          {
            fieldName: "Betaalwijze",
            visible: true,
            dataOverride: (data, field) => SummaryHelper.GetOptionLabelFromFieldByKeySummary(field, data)
          },
          {
            fieldName: "IBAN",
            visible: state.formData["Betaalwijze"]?.value === "INC"
          },
          {
            fieldName: "Tenaamstelling",
            visible: state.formData["Betaalwijze"]?.value === "INC"
          }
        ]
      },
      {
        title: getResourceString("Form.Contribution.Label"),
        contentClass: "summary-group__hide-label-mobile",
        rows: [
          {
            fieldName: "Contributie",
            labelOverride: getResourceString("Form.Contribution.Label"),
            visible: true,
            dataOverride: (data, field) => {
              return (
                state.formData["WatIsJeBrutoMaandinkomenStuderenDeeltijd"]?.value === "hoog" || 
                state.formData["WatIsJeBrutoMaandinkomenWerkendUitzendkracht"]?.value === "hoog" || 
                state.formData["WerkVorm"]?.value === "zzp" ?
                getResourceString("Form.Contribution.DiscountSummaryNoEvidence") : 
                getResourceString("Form.Contribution.DiscountSummary")
              ).replace(`{price}`, state.pageModel.contribution.price.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }))
                .replace(`{months}`, state.pageModel.contribution.monthlyPeriod.toString())
                .replace(`{priceDiscount}`, state.pageModel.contribution.discountPrice.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }));
            }
          }
        ]
      }
    ]
  };

   const removeUnwantedNodes = (node) => {
    if(Array.isArray(node)) {
      return node.map(removeUnwantedNodes)
      .filter(Boolean);
    }

    if ((node.type === 'text' && node.data === '\t') || node == '\t') {
      return null;
    }

    if (node.props?.children) {
      const newChildren = node.props.children
        .map(removeUnwantedNodes)
        .filter(Boolean);

      // due to restriction being read-only, node.props.children is not allowed to override
      // this is the only way to do this
      return {
        ...node,
        props: {
          ...node.props,
          children: newChildren,
        },
      };
    }

    return node;
  };

  const renderContent = ({windowWidth, contribution}) => {
    
    let parsedContentHtml = ReactHtmlParser(DOMPurify.sanitize(state.pageModel.content.replace(/\s\s+/g, ' ')));

    parsedContentHtml = removeUnwantedNodes(parsedContentHtml);

    if(windowWidth <= 769) {
      document.body.style.marginBottom = mobileLidmaatschapOpen ?  "300px" : "100px";
    }
    else {
      document.body.style.marginBottom = null;
    }


    // strip tags when only deeper value is needed
    const stripTags = (children) => {
      if (!children) return '';

      return React.Children.map(children, (child) => {
          if (typeof child === 'string') {
              return child;
          }
          if (child.props && child.props.children) {
              return stripTags(child.props.children);
          }
          return '';
      }).join('');
    };

    // Function to restructure the HTML as needed
    const restructureHtml = () => {
      return React.Children.map(parsedContentHtml, (child, index) => {
        
        switch (child.type) {
          case 'ul':
            return (
              <div key={`ul-${index}`}>
                {React.Children.map(child.props.children, (listItem, listIndex) => (
                    <div className="bullet-wrapper" key={`li-${listIndex}`}>
                      <span className="bullet-icon green"><i className="icon-checkmark"></i></span>
                      <span className="bullet-text">{stripTags(listItem)}</span>
                    </div>
                ))}
              </div>
            );
          default:
            break;
        }

        return child; // Return unchanged elements
      });
    };
  
    return (
      <div className="fnv-form-content__wrapper sticky sticky__top-60 fixed-md">
  
        <div className="fnv-form-content fnv-form-content__membership p-3">
        
          {(windowWidth > 769 || mobileLidmaatschapOpen) && (
            <h4 className="mb-3 mt-2">{getResourceString("Form.Lidworden.Lidmaatschap.Label")}</h4>
          )}
          <div className="discount-text">
            <span className="discount-text__highlighted">{getResourceString("Form.Lidworden.Lidmaatschap.FirstXMonths").replace(`{months}`, contribution?.monthlyPeriod?.toString() ?? "2")}</span>
            <span className="discount-text__highlighted green">
              €{contribution?.discountPrice?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }) ?? "-.--"} p/m
            </span>
          </div>
          <div className="discount-text">
            <span>{getResourceString("Form.Lidworden.Lidmaatschap.AfterXMonths")}</span>
            <span>
              €{contribution?.price?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }) ?? "-.--"} p/m
            </span>
          </div>
          <div className="discount-badge">{getResourceString("Form.Lidworden.Lidmaatschap.DiscountPercentage").replace(`{discount}`, contribution?.discountProcentage ?? "50")}</div>
          <div className="discount-icon">
            <button
                onClick={() => setMobileLidmaatschapOpen(!mobileLidmaatschapOpen)}
                aria-controls="fnv_content__collapse"
                aria-expanded={mobileLidmaatschapOpen}
                className="btn-icon btn-plain"
              >
              <i className={ mobileLidmaatschapOpen ? "icon2-cross" : "icon-open-chevron-up" }></i>
            </button>
          </div>
        </div>
  
        <Collapse in={(windowWidth > 769 ? true : mobileLidmaatschapOpen)}>
          <div id="fnv_content__collapse">
            <div className="spacer px-3"></div>
            <div className="fnv-form-content fnv-form-content__information p-3">
              {restructureHtml()}
            </div>
          </div>
        </Collapse>
      </div>
    );
  };

  return (
    <div className="form-redesign">
      {state.formDefinition && (
        <>
          <Steps cssClass={""}
            steps={state.formDefinitions.form.sections.map((section, index) => { return { title: getResourceString(`Form.Sections.${section["@guid"]}`), formName: index.toString()}; })}
            extraSteps={ [] }
            activeStepFormName={state.currentStepIndex.toString()}>
          </Steps>

          <div className="mijn-fnv animation__fade-in animation__fade-in--trigger">
            <div className="container content editpage-container__layout-modern p-0 p-md-2">
              <div className="row mijn-fnv__edit-page">
                <div className="col-12 col-md-1"></div>
                <div className="col-12 col-md-12 col-lg-10 p-0 p-md-2 flex flex-column flex-md-row flex-wrap-reverse">
                  <div className="col-12 mijn-fnv__edit-page col-md-8 form--no-page-title p-0 p-md-1">
                    {state.currentStepIndex == 0 && (<div className="fnv-form-content fnv-form-content__container p-4">
                      <h1 className={contentData?.showLineBelowTitleBlock ? "content__title-with-subtitle" : ''}>
                        {contentData?.titel}
                      </h1>
                      {!ValidationControlHelper.isNullOrEmpty(contentData?.subTitle) && (
                        <h2 className="content__subtitle">{contentData.subTitle}</h2>
                      )}
                      {!ValidationControlHelper.isNullOrEmpty(contentData?.introText) && (
                        <p className="content__intro">{ReactHtmlParser(contentData.introText)}</p>
                      )}
                      {!ValidationControlHelper.isNullOrEmpty(contentData?.content)  && !contentData?.contentAfterForm && (
                        <div className="content__before-form">
                          {ReactHtmlParser(contentData.content)}
                        </div>
                      )}
                    </div>)}
                    <div className="fnv-form__container form-container">
                      <form className={`form form__react ${state.formLoading ? `loading-div` : ``}`}>

                        <div className="col-12 col-sm p-4" ref={state.formFieldsRef}>
                          {!state.formLoading && <RenderStep
                              formDefinition={state.formDefinitions}
                              fields={state.fields}
                              visibleFields={state.visibleFields} 
                              currentStepIndex={state.currentStepIndex} 
                              formData={state.formData} 
                              setFormData={state.setFormData}
                              formErrors={state.formErrors} 
                              handleChange={state.handleChange} 
                              handleBlur={state.handleBlur}
                              formSummaryData={formSummaryData}
                              navigateToStep={state.setHandleFormStepSpecific}
                            />
                          }
                          <div className="row no-gutters mt-3">
                            {(() => {
                              if(state.currentStepIndex !== 0){
                                return (  
                                  <div className="col-auto form__vorig-stap-col">
                                    <a className="form__vorig-stap" href="#" onClick={state.handlePrevStep}><span className="icon icon-open-chevron-left"></span>{getResourceString("Form.Step.Previous")}</a>
                                  </div>
                                );
                              }
                            })()}
                            <div className="col d-flex justify-content-end form__button-next-step">
                              {(() => {
                                if(state.currentStepIndex !== state.formDefinitions.form.sections.length - 1){
                                  return (  
                                    <button type="button" className="btn btn-success" onClick={state.handleNextStep}>{getResourceString("Form.Step.Next")}<span className="icon icon-open-chevron-right"></span></button>
                                  );
                                }
                                else {
                                  return (
                                    <button type="submit" className="btn btn-success btn-form-submit" onClick={state.handleSubmit}>{getResourceString("Form.Send.ButtonCaption")}</button>
                                  );
                                }
                              })()}
                            </div>
                          </div>

                        </div>
                        
                      </form>

                      {state.loading && (
                        <div className="loader-new-overlay">
                          <span className="loader-new"></span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-12 col-md-4 p-0 p-md-1">
                    {renderContent({windowWidth: state.windowWidth, contribution: state.pageModel.contribution})}
                  </div>
                </div>
                <div className="col-12 col-md-1"></div>
              </div>
            </div>
          </div>
        </>
      )}
      
    </div>
  );
};

export default withFormState(WordLidForm);